
import { AlertCircle, CheckCircle } from "lucide-react";
import { format, addDays } from "date-fns";
import { PaymentStatusLoading } from "./LoadingStates";
import { Badge } from "@/components/ui/badge";

interface PaymentStatusProps {
  paymentStatus: boolean;
  lastPaymentDate?: string;
  validityDays?: number;
  isLoading?: boolean;
  planName?: string;
}

const planStyles: Record<string, { badge: string; glow: string; shimmer: string }> = {
  BLACK: {
    badge: "bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white border-gray-700 shadow-[0_0_15px_rgba(0,0,0,0.5)]",
    glow: "absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer bg-[length:200%_100%]",
    shimmer: "✦",
  },
  PLATINUM: {
    badge: "bg-gradient-to-r from-slate-300 via-slate-100 to-slate-300 text-slate-800 border-slate-400 shadow-[0_0_12px_rgba(148,163,184,0.4)]",
    glow: "absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer bg-[length:200%_100%]",
    shimmer: "◆",
  },
  GOLD: {
    badge: "bg-gradient-to-r from-amber-500 via-yellow-300 to-amber-500 text-amber-900 border-amber-500 shadow-[0_0_15px_rgba(245,158,11,0.4)]",
    glow: "absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer bg-[length:200%_100%]",
    shimmer: "★",
  },
  SILVER: {
    badge: "bg-gradient-to-r from-gray-300 via-gray-100 to-gray-300 text-gray-700 border-gray-300 shadow-[0_0_10px_rgba(156,163,175,0.3)]",
    glow: "absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-white/25 to-transparent animate-shimmer bg-[length:200%_100%]",
    shimmer: "●",
  },
};

const getPlanTier = (plan: string): string | null => {
  const upper = plan.toUpperCase();
  for (const tier of ["BLACK", "PLATINUM", "GOLD", "SILVER"]) {
    if (upper.includes(tier)) return tier;
  }
  return null;
};

export const PaymentStatus = ({ paymentStatus, lastPaymentDate, validityDays, isLoading, planName }: PaymentStatusProps) => {
  if (isLoading) {
    return <PaymentStatusLoading />;
  }

  return (
    <div className="space-y-4 text-center">
      <div className={`inline-flex items-center justify-center p-4 rounded-full mb-4 
        ${paymentStatus 
          ? 'bg-green-500/20 dark:bg-green-500/30 ocean:bg-green-500/30 sunset:bg-green-500/30 forest:bg-green-400/30' 
          : 'bg-clubbers-red/20 dark:bg-red-500/30 ocean:bg-red-500/30 sunset:bg-red-500/30 forest:bg-red-500/30'
        }`}>
        {paymentStatus ? (
          <CheckCircle className="w-8 h-8 text-green-500 dark:text-green-400 ocean:text-green-400 sunset:text-green-400 forest:text-green-300" />
        ) : (
          <AlertCircle className="w-8 h-8 text-clubbers-red dark:text-red-400 ocean:text-red-300 sunset:text-red-300 forest:text-red-300" />
        )}
      </div>
      <h2 className="text-2xl font-heading text-foreground">Estado del Pago:</h2>
      <p className={`text-3xl font-bold ${
        paymentStatus 
          ? 'text-green-600 dark:text-green-400 ocean:text-green-400 sunset:text-green-400 forest:text-green-400' 
          : 'text-clubbers-red dark:text-red-400 ocean:text-red-400 sunset:text-red-300 forest:text-red-400'
      }`}>
        {paymentStatus ? 'AL DÍA' : 'PENDIENTE'}
      </p>
      {paymentStatus && planName && getPlanTier(planName) && (() => {
        const tier = getPlanTier(planName)!;
        const style = planStyles[tier];
        return (
          <Badge className={`relative overflow-hidden text-sm px-5 py-1.5 font-bold tracking-wider ${style.badge}`}>
            <span className={style.glow} />
            <span className="relative z-10 flex items-center gap-1.5">
              <span className="text-xs opacity-70">{style.shimmer}</span>
              {tier}
              <span className="text-xs opacity-70">{style.shimmer}</span>
            </span>
          </Badge>
        );
      })()}
      {lastPaymentDate && validityDays && (
        <p className="text-foreground/70">
          (Vence el: {format(addDays(new Date(lastPaymentDate), validityDays), "dd/MM/yyyy")})
        </p>
      )}
    </div>
  );
};
